/**
 * 营地天赋树（《Fast Ludo 营地天赋树 V3.3》）管端这一侧：升级线说明、节点表校验、每个节点「升级前 → 升级后」。
 *
 * 《建造天赋树 V5.5》（2026-10-16）：加骰子恢复 / 每日赠卡 / 一次性礼包 / 每日拼图提示 / 自动掷骰五条线、倍率改上限制。
 *
 * 校验与服务端 game/talent.ts 的 validateTalentNodes 同口径——这里是给运营边填边看的红字，
 * 真正的拦截在服务端写接口与发布闸。线的清单与服务端 TALENT_LINES 一致，改一边要改另一边。
 */
import type { MsgKey, Translate } from "./i18n";

export interface TalentNode {
  /** 节点图标素材地址；空 = 未配置。 */
  icon?: string;
  /** 节点编号，形如 TAL_01。进资源块、进领取请求 */
  nodeId: string;
  /** 领取顺序，1 起连续 */
  seq: number;
  line: string;
  /** 领了之后这条线的值（单位随线） */
  value: number;
  /**
   * 解锁需要的天赋等级。萌宠小镇 = 小镇等级（每「几星一级」一档），界面上按「解锁累计星数」填，换算见 TalentPage；
   * 关卡制 = 升满的建筑数（前面各关栋数之和 + 当前关升满且没被破坏的栋数），界面上直接填
   */
  unlockLevel: number;
  nameI18n: Record<string, string>;
  textI18n: Record<string, string>;
  /** 礼包线（line = gift）领取时发的东西；其余线不带 */
  rewards?: GiftItem[];
}

/** 礼包里的一项：spinCap / moneyStar 的 qty 是百分比，其余是个数（服务端 game/talent.ts GiftRewardItem） */
export interface GiftItem { kind: string; qty: number }
/** 礼包类型（服务端 GIFT_REWARD_KINDS）。coins 为固定金币，moneyStar 保留按单星费用给 */
export const GIFT_KINDS = ["spin", "gems", "puzzle", "shield", "coins", "spinCap", "moneyStar"] as const;
export const GIFT_PCT_KINDS: ReadonlySet<string> = new Set(["spinCap", "moneyStar"]);
export const GIFT_LINE = "gift";
/** 一个礼包最多几项（服务端 GIFT_MAX_ITEMS） */
export const GIFT_MAX_ITEMS = 20;

/** 一个礼包的问题（界面文案 key）：空、数量不是正整数、类型不认识 */
export function giftWorse(items: GiftItem[] | undefined): MsgKey | null {
  const list = items ?? [];
  if (!list.length) return "talentx.worse.giftEmpty";
  if (list.some((it) => !(GIFT_KINDS as readonly string[]).includes(it.kind))) return "talentx.worse.giftKind";
  if (list.some((it) => !Number.isInteger(it.qty) || it.qty <= 0)) return "talentx.worse.giftQty";
  if (list.length > GIFT_MAX_ITEMS) return "talentx.worse.giftMax";
  return null;
}

export interface TalentLineMeta {
  id: string;
  /** 以下四项是界面文案的 key，渲染处 t() */
  name: MsgKey;
  building: MsgKey;
  unit: MsgKey;
  /** 起点从哪来 */
  base: MsgKey;
  /** true = 越大越好；false = 越小越好（生长间隔） */
  up: boolean;
  /** false = 本版只记领取状态，效果还没接 */
  live: boolean;
  /** 百分比线允许两位小数 */
  decimal: boolean;
}

export const TALENT_LINE_META: TalentLineMeta[] = [
  { id: "stake_tier", name: "talentx.line.stake_tier", building: "talentx.bld.diceTower", unit: "talentx.unit.x", base: "talentx.base.stake_tier", up: true, live: true, decimal: false },
  { id: "tree_growth", name: "talentx.line.tree_growth", building: "talentx.bld.tree", unit: "talentx.unit.minPerPt", base: "talentx.base.tree_growth", up: false, live: true, decimal: false },
  { id: "stamina_cap", name: "talentx.line.stamina_cap", building: "talentx.bld.tree", unit: "talentx.unit.pt", base: "talentx.base.stamina_cap", up: true, live: true, decimal: false },
  { id: "shield_slot", name: "talentx.line.shield_slot", building: "talentx.bld.wall", unit: "talentx.unit.count", base: "talentx.base.shield_slot", up: true, live: true, decimal: false },
  { id: "sr_rate", name: "talentx.line.sr_rate", building: "talentx.bld.petNest", unit: "talentx.unit.pctRel", base: "talentx.base.sr_rate", up: true, live: false, decimal: true },
  { id: "tree_cap", name: "talentx.line.tree_cap", building: "talentx.bld.tree", unit: "talentx.unit.ptPerTree", base: "talentx.base.tree_cap", up: true, live: true, decimal: false },
  { id: "friend_slots", name: "talentx.line.friend_slots", building: "talentx.bld.pigeon", unit: "talentx.unit.people", base: "talentx.base.friend_slots", up: true, live: true, decimal: false },
  { id: "match_bonus", name: "talentx.line.match_bonus", building: "talentx.bld.mainTent", unit: "talentx.unit.pct", base: "talentx.base.match_bonus", up: true, live: false, decimal: true },
  { id: "raid_yield", name: "talentx.line.raid_yield", building: "talentx.bld.cannon", unit: "talentx.unit.pct", base: "talentx.base.raid_yield", up: true, live: false, decimal: true },
  { id: "tree_count", name: "talentx.line.tree_count", building: "talentx.bld.tree", unit: "talentx.unit.trees", base: "talentx.base.tree_count", up: true, live: true, decimal: false },
  { id: "daily_stamina", name: "talentx.line.daily_stamina", building: "talentx.bld.tree", unit: "talentx.unit.pt", base: "talentx.base.daily_stamina", up: true, live: true, decimal: false },
  { id: "dice_regen", name: "talentx.line.dice_regen", building: "talentx.bld.diceTower", unit: "talentx.unit.secPerDie", base: "talentx.base.dice_regen", up: false, live: true, decimal: false },
  { id: "gift_cap", name: "talentx.line.gift_cap", building: "talentx.bld.pigeon", unit: "talentx.unit.cardsPerDay", base: "talentx.base.gift_cap", up: true, live: true, decimal: false },
  { id: "gift", name: "talentx.line.gift", building: "talentx.bld.none", unit: "talentx.unit.gift", base: "talentx.base.gift", up: true, live: true, decimal: false },
  { id: "puzzle_hint", name: "talentx.line.puzzle_hint", building: "talentx.bld.idol", unit: "talentx.unit.timesPerDay", base: "talentx.base.puzzle_hint", up: true, live: true, decimal: false },
  { id: "auto_roll", name: "talentx.line.auto_roll", building: "talentx.bld.diceTower", unit: "talentx.unit.unlock", base: "talentx.base.auto_roll", up: true, live: true, decimal: false },
  { id: "card_pity", name: "talentx.line.card_pity", building: "talentx.bld.none", unit: "talentx.unit.pt", base: "talentx.base.card_pity", up: true, live: true, decimal: false },
  { id: "collection_complete", name: "talentx.line.collection_complete", building: "talentx.bld.none", unit: "talentx.unit.unlock", base: "talentx.base.collection_complete", up: true, live: false, decimal: false },
];
export const TALENT_LINE: Record<string, TalentLineMeta> = Object.fromEntries(TALENT_LINE_META.map((l) => [l.id, l]));
/** 体力树的三条线：关卡制没有体力树，不能配（服务端 game/talent.ts TREE_LINES，发布闸 TALENT_TREE_LINE_LEVELS） */
export const TALENT_TREE_LINES: ReadonlySet<string> = new Set(["tree_growth", "tree_cap", "tree_count"]);
/** 这个体系能配的升级线 */
export const talentLinesOf = (system: "levels" | "pettown"): TalentLineMeta[] =>
  system === "levels" ? TALENT_LINE_META.filter((l) => !TALENT_TREE_LINES.has(l.id)) : TALENT_LINE_META;
export const TALENT_NODE_MAX = 500;
const NODE_ID_RE = /^[A-Za-z0-9_-]{1,16}$/;

/** 各线在树外的起点。拿不到的（没配体力树等）为 null：界面显示「—」，只核同线之间 */
export function talentStart(
  common: { shieldMax: number | null; friendLimit: number | null; stakeTierBase?: number | null; diceRegenSec?: number | null; giftCap?: number | null },
  tree: { treeGrowSec: number | null; treeCap: number | null; treeCount: number | null } | null,
): Record<string, number | null> {
  return {
    stake_tier: common.stakeTierBase != null && common.stakeTierBase >= 1 ? common.stakeTierBase : 1,
    tree_growth: tree?.treeGrowSec ? tree.treeGrowSec / 60 : null,
    stamina_cap: 0,
    shield_slot: common.shieldMax != null && common.shieldMax >= 0 ? common.shieldMax : null,
    sr_rate: 0,
    tree_cap: tree?.treeCap ?? null,
    friend_slots: common.friendLimit != null && common.friendLimit > 0 ? common.friendLimit : null,
    match_bonus: 0,
    raid_yield: 0,
    tree_count: tree?.treeCount ?? null,
    daily_stamina: 0,
    dice_regen: common.diceRegenSec != null && common.diceRegenSec > 0 ? common.diceRegenSec : null,
    // 0 = 不限：赠卡节点不能配（talentNodeIssues 报），起点按「拿不到」显示
    gift_cap: common.giftCap != null && common.giftCap > 0 ? common.giftCap : null,
    gift: null,
    puzzle_hint: 0,
    auto_roll: 0,
  };
}

export interface TalentRow extends TalentNode {
  /** 这条线第几个节点 */
  level: number;
  /** 升级前的值（同线上一个节点，没有就是起点） */
  before: number | null;
  /** 数值有问题时的一句话（界面文案 key） */
  worse: MsgKey | null;
}

export function deriveTalent(nodes: TalentNode[], start: Record<string, number | null>): TalentRow[] {
  const seen: Record<string, { value: number; level: number }> = {};
  return [...nodes].sort((a, b) => a.seq - b.seq).map((n) => {
    // 礼包不参与「一级比一级好」，只看内容
    if (n.line === GIFT_LINE) return { ...n, level: 1, before: null, worse: giftWorse(n.rewards) };
    const prev = seen[n.line];
    const before = prev ? prev.value : start[n.line] ?? null;
    const level = prev ? prev.level + 1 : 1;
    const L = TALENT_LINE[n.line];
    let worse: MsgKey | null = null;
    if (!Number.isFinite(n.value)) worse = "talentx.worse.empty";
    else if (L && !L.decimal && !Number.isInteger(n.value)) worse = "talentx.worse.int";
    else if (L && L.decimal && Math.round(n.value * 100) !== n.value * 100) worse = "talentx.worse.decimal";
    else if (L && before != null && (L.up ? n.value <= before : n.value >= before)) worse = L.up ? "talentx.worse.mustUp" : "talentx.worse.mustDown";
    else if ((n.line === "tree_growth" || n.line === "dice_regen") && !(n.value > 0)) worse = "talentx.worse.positive";
    seen[n.line] = { value: n.value, level };
    return { ...n, level, before, worse };
  });
}

/**
 * 整表的问题（与服务端写接口 / 发布闸同口径），界面顶部红字。
 * levels = 关卡制：解锁按天赋等级说，体力树三条线不许配，maxLevel（全部升满的等级）给了就核够不够得着
 */
export function talentNodeIssues(t: Translate, nodes: TalentNode[], start: Record<string, number | null>,
  levels?: { maxLevel: number | null }, giftCapUnlimited = false): string[] {
  const unit = t(levels ? "talentx.unlockLevel" : "talentx.unlockStars");
  const out: string[] = [];
  const sorted = [...nodes].sort((a, b) => a.seq - b.seq);
  const ids = new Set<string>();
  sorted.forEach((n, i) => {
    if (!NODE_ID_RE.test(n.nodeId)) out.push(t("talentx.issue.idFormat", { id: JSON.stringify(n.nodeId) }));
    else if (ids.has(n.nodeId)) out.push(t("talentx.issue.idDup", { id: n.nodeId }));
    ids.add(n.nodeId);
    if (n.seq !== i + 1) out.push(t("talentx.issue.seqGap"));
    if (!TALENT_LINE[n.line]) out.push(t("talentx.issue.unknownLine", { id: n.nodeId }));
    if (levels && TALENT_TREE_LINES.has(n.line)) out.push(t("talentx.issue.treeLine", { id: n.nodeId, line: TALENT_LINE[n.line] ? t(TALENT_LINE[n.line].name) : n.line }));
    if (!Number.isInteger(n.unlockLevel) || n.unlockLevel < 1) out.push(t("talentx.issue.unlockMin", { id: n.nodeId, unit }));
    else if (i > 0 && n.unlockLevel < sorted[i - 1].unlockLevel) out.push(t("talentx.issue.unlockDesc", { id: n.nodeId, unit }));
    if (!String(n.nameI18n?.en ?? "").trim()) out.push(t("talentx.issue.nameEn", { id: n.nodeId }));
  });
  if (giftCapUnlimited && nodes.some((n) => n.line === "gift_cap")) out.push(t("talentx.issue.giftCapZero"));
  const bad = deriveTalent(nodes, start).filter((r) => r.worse).length;
  if (bad) out.push(t("talentx.issue.badValues", { n: bad }));
  return [...new Set(out)];
}

/** 新增一个节点：接在最后，编号取下一个没用过的 TAL_xx，解锁星数沿用最后一个 */
export function newTalentNode(xs: TalentNode[]): TalentNode {
  const seq = xs.length + 1;
  let n = seq, nodeId = `TAL_${String(n).padStart(2, "0")}`;
  while (xs.some((x) => x.nodeId === nodeId)) nodeId = `TAL_${String(++n).padStart(2, "0")}`;
  const last = [...xs].sort((a, b) => a.seq - b.seq)[xs.length - 1];
  return { icon: "", nodeId, seq, line: "stake_tier", value: 0, unlockLevel: last?.unlockLevel ?? 1, nameI18n: { en: "" }, textI18n: {} };
}

/**
 * 多人代理里体力倍率线的节点值是体力档位（颗），按 ×N = 档位 ÷ 10 显示（用户 2026-10-26：
 * 10 / 20 / 50 / 100 / 200 / 500 / 1000 ↔ ×1 / ×2 / ×5 / ×10 / ×20 / ×50 / ×100）。只是显示，存的仍是档位
 */
export const MATCH_STAKE_PER_X = 10;
export const fmtMatchStake = (tier: number | null): string =>
  tier == null || !Number.isFinite(tier) ? "—" : `×${tier / MATCH_STAKE_PER_X}`;

/** 数值展示：倍率 ×N、加成类 +N、百分比 +N% */
export function fmtTalent(line: string, v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  if (line === "stake_tier") return `×${v}`;
  if (line === "dice_regen") return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
  if (line === "stamina_cap" || line === "daily_stamina") return `+${v}`;
  if (line === "sr_rate" || line === "match_bonus" || line === "raid_yield") return `+${v}%`;
  return String(v);
}
